import { ProfileProps } from '@common/types';
import ErrorFallback from '@components/ErrorFallback';
import Header from '@components/Header';
import ContactStatusToast from '@components/Profile_New/ContactStatusToast';
import ProfileTemplates from '@components/Profile_New/ProfileTemplates/ProfileTemplates';
import SectionWrapper from '@components/Profile_New/SectionWrapper';
import {
  PAGE_NAMES,
  PROFILE_PAGE_DATA_HOOKS,
} from '@utils/partnerProfileUtils';
import { Box, Divider } from '@wix/design-system';
import { useIntersect } from '@wix/experts-common';
import clsx from 'clsx';
import CopyNotification from 'components/Profile_New/CopyNotification';
import { FC, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import About from './About';
import BackToExplorer from './BackToExplorer';
import ClientProjects from './ClientProjects';
import ContactPanel from './ContactPanel';
import MiniContactPanel from './MiniContactPanel';
import NavBar from './NavBar';
import { ProfileNavbarSection } from './NavBar/NavBar';
import s from './Profile.module.scss';
import StartingRates from './StartingRates';
import ProjectsReviews from './ProjectsReviews';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';
import { useProfileSections } from './hooks/useProfileSections';

const ProfileMobile: FC<ProfileProps> = ({
  onContactClicked,
  onCopyClicked,
  headerOnClick,
  copyStatus,
  setCopyStatus,
  showBack,
  certifications,
  isCertificationsLoading,
  scrollToSection,
  sessionId,
  isRenderedInModal,
  numberOfTemplates,
  navigationRefs,
  copy,
  profileUrl,
  contactStatus,
  clearStatusMap,
}) => {
  const {
    shouldShowClientProjects,
    shouldShowServices,
    shouldShowReviews,
    shouldShowTemplates,
    shouldShowServicesAndReviews,
    getValidActiveTab,
  } = useProfileSections(numberOfTemplates);

  const [ref] = useIntersect({}) as any;
  const [isMiniContactPanelVisible, setIsMiniContactPanelVisible] =
    useState(false);
  const [activeTab, setActiveTab] = useState<ProfileNavbarSection>(
    getValidActiveTab() as ProfileNavbarSection,
  );

  const handleMobileTabChange = (tab: string) => {
    setActiveTab(tab as ProfileNavbarSection);
    window.scrollTo(0, 0);
  };

  return (
    <Box
      display="block"
      dataHook={PROFILE_PAGE_DATA_HOOKS.MOBILE_PROFILE_PAGE}
      className={`${s.mobileContainer} ${s.profileAccessibility}`}
    >
      {!isRenderedInModal ? (
        <div className={`${s.stickyHeader} ${s.mobile}`}>
          <Header
            onClick={headerOnClick}
            pageName={PAGE_NAMES.PROFILE_PAGE}
            sessionId={sessionId}
          />
          <Divider skin="light" />
        </div>
      ) : null}
      <ContactStatusToast
        status={contactStatus}
        clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
        isMobileView={true}
      />
      <CopyNotification
        status={copyStatus}
        clearStatus={() => setCopyStatus(null)}
        isMobileView={true}
      />
      <BackToExplorer showBack={showBack} isMobileView={true} />
      <Box paddingTop={showBack ? '0px' : '30px'}>
        <ContactPanel
          isMobileView={true}
          onContactClicked={onContactClicked}
          copy={copy}
          onCopyClicked={onCopyClicked}
          shareUrl={profileUrl}
          certifications={certifications}
        />
      </Box>
      <div ref={ref} className={clsx(s.stickyMiniContactPanel)}>
        <MiniContactPanel
          onContactClicked={onContactClicked}
          copy={copy}
          onCopyClicked={onCopyClicked}
          shareUrl={profileUrl}
          onVisibilityChange={setIsMiniContactPanelVisible}
        />
        {isMiniContactPanelVisible && <Divider skin="light" />}
      </div>
      <NavBar
        isMobileView={true}
        navigationRefs={navigationRefs}
        onTabChange={handleMobileTabChange}
        activeTab={activeTab}
        isMiniContactPanelVisible={isMiniContactPanelVisible}
      />
      <Box
        dataHook="profile-page-sections"
        className={`${s.profilePageSections} ${s.mobile}`}
        direction="vertical"
      >
        {activeTab === PROFILE_PAGE_SECTIONS.ABOUT && (
          <SectionWrapper
            sectionId={PROFILE_PAGE_SECTIONS.ABOUT}
            ref={navigationRefs}
          >
            <About
              isMobileView={true}
              certifications={certifications}
              isCertificationsLoading={isCertificationsLoading}
              onShowLess={() =>
                scrollToSection(
                  PROFILE_PAGE_SECTIONS.ABOUT,
                  isMiniContactPanelVisible,
                )
              }
            />
          </SectionWrapper>
        )}

        {activeTab === PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS && (
          <SectionWrapper
            sectionId={PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS}
            ref={navigationRefs}
            visible={shouldShowClientProjects}
          >
            <ClientProjects
              isMobileView={true}
              onShowLess={() =>
                scrollToSection(
                  PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS,
                  isMiniContactPanelVisible,
                )
              }
            />
          </SectionWrapper>
        )}

        {activeTab === PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS && (
          <SectionWrapper
            sectionId={PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS}
            ref={navigationRefs}
            visible={shouldShowServicesAndReviews}
            className={s.servicesAndReviewsSection}
          >
            <Box direction="vertical" gap="54px">
              <SectionWrapper
                sectionId={PROFILE_PAGE_SECTIONS.SERVICES}
                ref={navigationRefs}
                visible={shouldShowServices}
              >
                <StartingRates isMobileView={true} />
              </SectionWrapper>
              <SectionWrapper
                sectionId={PROFILE_PAGE_SECTIONS.REVIEWS}
                ref={navigationRefs}
                visible={shouldShowReviews}
              >
                <ProjectsReviews
                  isMobileView={true}
                  onPageChange={() =>
                    scrollToSection(
                      PROFILE_PAGE_SECTIONS.REVIEWS,
                      isMiniContactPanelVisible,
                    )
                  }
                />
              </SectionWrapper>
            </Box>
          </SectionWrapper>
        )}

        {activeTab === PROFILE_PAGE_SECTIONS.TEMPLATES && (
          <SectionWrapper
            sectionId={PROFILE_PAGE_SECTIONS.TEMPLATES}
            ref={navigationRefs}
            visible={shouldShowTemplates}
          >
            <ProfileTemplates
              isMobileView={true}
              onShowLess={() =>
                scrollToSection(
                  PROFILE_PAGE_SECTIONS.TEMPLATES,
                  isMiniContactPanelVisible,
                )
              }
            />
          </SectionWrapper>
        )}
      </Box>
    </Box>
  );
};

export default ProfileMobile;
